import { FormGroup } from '@angular/forms';
import {
  AnyRecommendee,
  RecommendeeType,
} from '@app/recommendations/recommendations.model';
import {
  UserGroupListService,
  UserOrGroupSearchItem,
} from '@app/shared/services/content/user-group-list.service';
import { onFormControlUpdate } from '@app/shared/utils/angular-form-field-helpers';
import { TranslateService } from '@ngx-translate/core';
import { map, Observable, of } from 'rxjs';

/**
 * Where we have a 'real' owner, get any necessary supporting details (userpic, name)
 * and add that to the input.
 *
 * @param input - Article/Video/Course, anywhere a content owner could be found.
 * @param service - UserGroupListService
 */
export const addOwnerDetailsToInput = <T>(
  input: any,
  service: UserGroupListService
): Observable<T & { owner: AnyRecommendee }> => {
  if (!input.primaryContactResourceId || !input.primaryContactResourceType) {
    return of(input);
  }

  // Where the type is group and the name is available, an extra API call is unnecessary.
  if (
    input.primaryContactResourceType === 'Group' &&
    input.primaryContactName
  ) {
    return of({
      ...input,
      owner: {
        resourceType: input.primaryContactResourceType,
        resourceId: input.primaryContactResourceId,
        name: input.primaryContactName,
      },
    });
  }
  // Otherwise, get the details and return them.
  return service
    .getUserOrGroupDetailsByKey(
      input.primaryContactResourceId,
      input.primaryContactResourceType,
      input.organizationId
    )
    .pipe(map((owner) => ({ ...input, owner }) as any));
};

/**
 * Where we have a simple string owner, set the name but leave other fields null.
 *
 * @param input
 */
export const addTempOwnerToInput = <T>(input: any): Observable<T> => {
  const owner: UserOrGroupSearchItem = {
    name: input.owner as any as string,
    resourceId: null,
    resourceType: null,
    organizationId: null,
    picture: null,
    vanityUrl: null,
  };
  return of({ ...input, owner });
};

export const parseOwnerDataForBE = (recommendee: AnyRecommendee) => ({
  primaryContactResourceId: recommendee?.resourceId,
  // GroupIdentifier.resourceType is `RecommendeeType | string` so we need to cast it until that is fixed
  primaryContactResourceType: recommendee?.resourceType as RecommendeeType,
  owner: recommendee?.name,
});

export const getContentOwnerError =
  (translateService: TranslateService) => (form: FormGroup) => {
    const error = form.get('owner').hasError('validContentOwnerNeeded')
      ? 'MediaFormCtrl_ValidContentOwnerRequired'
      : 'dgOrgInternalContentForm_ContentOwnerRequired';

    return translateService.instant(error);
  };

export const onContentOwnerBlur = (value: string, form: FormGroup) => {
  if (value) {
    // If blur is triggered and the input has a value, no user/group was seleted
    // so, trigger the `validContentOwnerNeeded` validation
    onFormControlUpdate(form, 'owner', value);
  } else {
    // Else the user just tabbed through, so trigger `required` validation
    form.get('owner').markAsTouched();
  }
};
